import { API, graphqlOperation } from "aws-amplify";

import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

import RoleForm from "components/roles/RoleForm";
import {
  createRolePermission,
  createRoleUser,
  deleteRolePermission,
  deleteRoleUser,
  updateRole,
} from "graphql/mutations";
import { customGetRole } from "graphql/custom-queries";
import { Loader } from "@aws-amplify/ui-react";
import { rolePermissionsByRoleId, roleUsersByRoleId } from "graphql/queries";

const RoleUpdateForm = ({ roleId, onSuccess }) => {
  const navigate = useNavigate();
  const [role, setRole] = useState();

  async function onSubmit(values) {
    try {
      const { name, description, users, permissions, companyID } = values;
      const promises = [];

      const permissionsToUnLink = role.permissions
        ?.filter(
          (roleUs) =>
            !permissions?.some((permission) => permission.id === roleUs.id)
        )
        ?.map((permission) => permission.id);
      permissionsToUnLink?.forEach(async (permissionId) => {
        const rolePermissionRecords = (
          await API.graphql({
            query: rolePermissionsByRoleId.replaceAll("__typename", ""),
            variables: {
              roleId,
              limit: 250,
              filter: {
                and: [{ permissionId: { eq: permissionId } }],
              },
            },
          })
        )?.data?.rolePermissionsByRoleId?.items;
        promises.push(
          API.graphql({
            query: deleteRolePermission.replaceAll("__typename", ""),
            variables: {
              input: {
                id: rolePermissionRecords[0].id,
              },
            },
          })
        );
      });
      const permissionsToLink = permissions
        ?.filter(
          (permission) =>
            !role.permissions?.some(
              (rolePermission) => rolePermission.id === permission.id
            )
        )
        ?.map((permission) => permission.id);
      permissionsToLink?.forEach((permissionId) => {
        promises.push(
          API.graphql({
            query: createRolePermission.replaceAll("__typename", ""),
            variables: {
              input: {
                roleId,
                permissionId,
              },
            },
          })
        );
      });

      const usersToUnLink = role.users
        ?.filter((roleUs) => !users?.some((user) => user.id === roleUs.id))
        ?.map((user) => user.id);
      usersToUnLink?.forEach(async (userId) => {
        const roleUserRecords = (
          await API.graphql({
            query: roleUsersByRoleId.replaceAll("__typename", ""),
            variables: {
              roleId,
              filter: {
                and: [{ userId: { eq: userId } }],
              },
            },
          })
        )?.data?.roleUsersByRoleId?.items;
        promises.push(
          API.graphql({
            query: deleteRoleUser.replaceAll("__typename", ""),
            variables: {
              input: {
                id: roleUserRecords[0].id,
              },
            },
          })
        );
      });
      const usersToLink = users
        ?.filter(
          (user) => !role.users?.some((roleUser) => roleUser.id === user.id)
        )
        ?.map((user) => user.id);
      usersToLink?.forEach((userId) => {
        promises.push(
          API.graphql({
            query: createRoleUser.replaceAll("__typename", ""),
            variables: {
              input: {
                roleId,
                userId,
              },
            },
          })
        );
      });

      promises.push(
        API.graphql({
          query: updateRole.replaceAll("__typename", ""),
          variables: {
            input: {
              id: roleId,
              name,
              description,
              companyID,
            },
          },
        })
      );
      await Promise.all(promises);
      toast.success(strings.successUpdated);
      if (onSuccess) onSuccess();
      else navigate("/roles");
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  async function fetchRole(id) {
    try {
      const roleData = await API.graphql(
        graphqlOperation(customGetRole, { id })
      );
      const roleItem = roleData.data.getRole;
      if (!roleItem) {
        toast.error(strings.responses.errorFetch);
        navigate("/roles");
        return;
      }
      setRole({
        name: roleItem.name,
        description: roleItem.description,
        users: roleItem.users.items?.map((roleUs) => ({
          id: roleUs.user.id,
          name: roleUs.user.email,
        })),
        permissions: roleItem.permissions.items?.map((rolePer) => ({
          id: rolePer.permission.id,
          name: rolePer.permission.description || rolePer.permission.name,
        })),
        companyID: roleItem.companyID,
      });
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    }
  }

  useEffect(() => {
    fetchRole(roleId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleId]);

  if (!role) return <Loader />;

  return (
    <div>
      <RoleForm onSubmit={onSubmit} type="update" initialValues={role} />
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Actualizar Rol",
    successUpdated: "Rol actualizado con éxito",
    responses: {
      errorFetch: "Error al consultar el rol",
    },
  },
});

export default RoleUpdateForm;
